import { Coords, OrderStopType, PaymentMethod, Product, VehicleType } from "@app/modules/create-order/entities/create-order.entities";
import { OrderStatusResDTOV3, PickingPoint, RetailerOrigin, TransportMediaType } from "@core/entities";
import { Modify, PickKeysFromUnionType } from "@core/utils/ts.utils";
import { ActiveOrdersSelectionSetType, ArchivedOrdersSelectionSetType } from "../constants";

export enum OrdersType {
  ACTIVE = "ACTIVE",
  ARCHIVED = "ARCHIVED",
  PENDING = "PENDING",
  BOX_PENDING = "BOX_PENDING",
}

export enum ETAType {
  "USER" = "USER",
  "STOP" = "STOP",
  "STORE" = "STORE",
}

export interface ActiveOrderQueryParams {
  pickingPointId: number;
  inputSearch?: string;
  status?: number[];
  orderBy?: OrderBy;
}

export interface ArchivedOrderQueryParams extends ActiveOrderQueryParams {
  cursor?: number;
}

export interface SearchOrderQueryParams extends ArchivedOrderQueryParams {
  ordersType: OrdersType;
}

export interface OrderBy {
  name: string;
  type: "ASC" | "DESC";
}

export interface OrderColumn {
  id: string;
  optional: boolean;
  orderType?: "ACTIVE" | "ARCHIVED";
}

export interface OrdersState {
  ui: UiOrders;
  selectedOrderId: string;
  selectedOrder: DetailOrder | null;
  selectedPendingOrder: DetailOrder | null;
  selectedBoxPendingOrder: BoxPendingOrder | null;
  activeOrdersList: ActiveListOrder[];
  archivedOrdersList: ArchivedListOrder[];
  pendingOrdersList: ActiveListOrder[];
  pendingBoxOrdersList: BoxPendingOrder[];
  pendingOrdersSelectedPickingPoint?: PickingPoint;
  ordersType: OrdersType;
  searchInput?: string;
  orderDuplication: {
    body: null | any;
    parsedBody: null | any;
    aproximateCost: string | null;
    newTotalValue: string;
  };
}

export interface StatusFilterOption {
  status: string;
  status_ids: number[];
}

export interface UiOrders {
  loaderBtnCancel?: boolean;
  loadingOrderDetail?: boolean;
  loadingOrderList?: boolean;
  loadingExcelExport?: boolean;
  isModalCancelShow?: boolean;
  showRemoveDebtModal?: boolean;
  loaderBtn?: boolean;
  openDuplicateOrderDialog?: boolean;
  isLoading?: boolean;
  hasBoxPendingData: false;
  hasPendingData: false;
  hasArchivedData: false;
  hasActiveData: false;
  isForm?: boolean;
  deleteSuccess?: boolean;
  lastArchivedOrdersPage?: boolean;
  columns: string[];
  disableAutoRefresh: boolean;
  openColumnSettingsDialog: boolean;
  openExcelDialog: boolean;
  archivedOrdersCursor: number | null;
  childOrderDetail: boolean;
  previousArchivedOrdersCursors: (number | null)[];
}

export type ArchivedListOrder = Modify<
  ArchivedOrderListResponse & {
    showHandshake: boolean;
    isInverse: boolean;
  },
  {
    transport_media_type: TransportMediaType;
  }
>;

export type ActiveListOrder = Modify<
  ActiveOrderListResponse & {
    showHandoff: boolean;
    showHandshake: boolean;
    isInverse: boolean;
  },
  {
    transport_media_type: TransportMediaType;
  }
>;

export interface OrderPagination {
  perPage: number;
  currentPage: number;
  from: number;
  to: number;
  lastPage?: number;
}

export interface OrderUpdate {
  id: number;
  order_id: number;
  rappi_order_id: number;
  order_status_id: number;
  core_status: string;
  updated_at: string;
}
export interface OrderModification {
  created_at: string;
  id: number;
  order_id: number;
  type: string;
  rappi_order_id: number;
}

export interface DetailActionPoint {
  address: string;
  check_in_required: boolean;
  city: string;
  comments?: string;
  contact_email: string;
  contact_name: string;
  contact_phone: string;
  description: string;
  executed: boolean;
  external_picking_point_id: string | number | null;
  id: string;
  index: number;
  instructions: string;
  lat: number;
  lng: number;
  picking_point_id: number | null;
  products: Product[];
  return_allowed: boolean;
  zip_code: any;
  picking_point_name: string;
  stop_type: OrderStopType;
}

export interface ETA {
  time: number;
  total: number;
  type: ETAType;
}

export interface EtaRanges {
  delivery_min_time: string;
  delivery_max_time: string;
  delivery_time: string;
  eta_publish: string;
}

export interface ChildOrder {
  created_at: string;
  external_order_id: string;
  id: number;
  order_status_id: number;
  rappi_order_id: number;
  total_value: number | undefined;
  handshake_code: string | null;
  handoff_code: string | null;
}

export interface DetailOrder {
  action_points: DetailActionPoint[];
  arrival_time: string;
  cancelation_reason: string;
  cancellation_username: string | null;
  cancelled_at: string;
  cargo_order_id: string;
  collect_cash: number;
  client: OrderClient;
  courier: Courier;
  created_at: string;
  debt_cancellation_user_name?: string;
  debt_cancelled_at?: string;
  deliver_at: string;
  eta_range?: EtaRanges | null;
  external_order_id: string;
  final_delivery_comments?: string;
  final_delivery_lat?: number;
  final_delivery_lng?: number;
  in_store_reference_id?: string;
  final_delivery_point_image?: string;
  handoff_code: string | null;
  handshake_code: string | null;
  order_modification: OrderModification[];
  order_status_id: number;
  order_ready_at?: string;
  order_update: OrderUpdate[];
  child_orders: ChildOrder[];
  parent_order_id?: string;
  origin: RetailerOrigin;
  partner_cancelation_reason: string | null;
  payment_method: PaymentMethod;
  price: {
    /** @deprecated No sumar con partner_incentive: se solapan. Usar courier_tip_total. */
    tip: number;
    total: number;
    /** @deprecated Ya incluye el default tip reportado en tip. Usar courier_tip_total. */
    partner_incentive?: number;
    /** Total de propina e incentivos financiado por el retailer que recibe el courier. */
    courier_tip_total?: number;
  };
  rappi_order_id: string;
  requires_handshake: boolean;
  retailer_id: string;
  state: string;
  sales_channel?: string;
  total_value: number;
  tracking_code: string;
  vehicle_type: VehicleType;
  version: number | null;
  was_cancelled_by_integration: boolean;
  was_processed_by_agent: boolean;
  isInverse?: boolean;
  showHandshake?: boolean;
  showHandoff?: boolean;
}

export interface OrderClient {
  address: string;
  email: string;
  first_name: string;
  last_name: string;
  lat: number;
  lng: number;
  phone: string;
}

export interface StoreKeeperProps {
  coords?: Coords;
  transport_media_type?: TransportMediaType;
}

export interface Courier {
  first_name: string;
  last_name: string;
  id: number;
  phone: string;
  lat: number;
  lng: number;
  transport_media_type: TransportMediaType;
}

export interface BoxPendingOrder {
  uuid: string;
  scheduled: boolean;
  merchant_id: string;
  external_reference: string;
  short_external_reference: string;
  origin: {
    id: string;
    name: string;
  };
  sub_total: number;
  total_price: number;
  final_total_price: number;
  delivery_fee: number;
  delivery_mode: "DELIVERY";
  delivered_by: "CARGO";
  ready_to_deliver: boolean;
  preparation_start: string;
  delivery_at: string;
  order_created_at: string;
  approved_at: string | null;
  status: {
    id: "CONFIRMED";
    name: "Confirmado";
  };
  scheduled_at: string | null;
  marketplace_name: string;
  observations: string;
  cancellation_requested: false;
  customer: {
    uuid: string;
    name: string;
    document: string;
    phone: string;
    orders_count_on_restaurant: number;
  };
  address: {
    formatted: string;
    country: string;
    state: string;
    city: string;
    neighborhood: string;
    street_name: string;
    postal_code: string;
    street_number: string;
    complement: string;
    lat: number;
    lng: number;
  };
  items: {
    uuid: string;
    name: string;
    quantity: number;
    price: number;
    sub_items_price: number;
    total_price: number;
    discount: number;
    addition: number;
    external_code: string;
    external_id: string;
    observations: string;
    height: number;
    width: number;
    length: number;
    weight: number;
  }[];
  payments: [];
  benefits: [];
  need_approve: boolean;
  confirmation_code: string | null;
  created_at: {
    date: string;
    timezone_type: number;
    timezone: string;
  };
}

export type ActiveOrderListResponse = PickKeysFromUnionType<OrderStatusResDTOV3, ActiveOrdersSelectionSetType[number]> & {
  picking_point: Partial<PickingPoint>;
};

export type ArchivedOrderListResponse = PickKeysFromUnionType<OrderStatusResDTOV3, ArchivedOrdersSelectionSetType[number]> & {
  picking_point: Partial<PickingPoint>;
};

export type AcceptPendingOrderBody = {
  preparationTime: number;
  orderId: string | number;
  filteredPickingPointId?: number;
};
